import { validateReportCitations } from './citationEnforcement';
import { computeDeepResearchConfidence } from './confidence';
import { createEmptyKnowledgeGraphMemory, updateKnowledgeGraphMemory } from './knowledgeGraphMemory';
import { assembleFinalResearchReport } from './reportAssembly';
import { validateAndNormalizeSources } from './sourceValidation';
import type {
  DeepResearchAgentRole,
  DeepResearchKnowledgeGraphMemory,
  DeepResearchRoleResult,
  DeepResearchRunInput,
  DeepResearchRunResult,
  DeepResearchSource,
  DeepResearchStageUpdate,
} from './types';

const roleTitles: Record<DeepResearchAgentRole, string> = {
  orchestrator: 'Orchestrator',
  planner: 'Research plan',
  web_research: 'Source collection',
  academic_research: 'Academic research',
  code_analysis: 'Code analysis',
  source_verification: 'Verification and confidence scoring',
  timeline_builder: 'Timeline building',
  citation_manager: 'Citation assembly',
  report_writer: 'Final report',
  knowledge_graph: 'Knowledge graph memory',
};

const createInitialResults = () => {
  const entries = [
    'orchestrator',
    'planner',
    'web_research',
    'academic_research',
    'code_analysis',
    'source_verification',
    'timeline_builder',
    'citation_manager',
    'report_writer',
    'knowledge_graph',
  ].map((role) => [
    role,
    {
      role,
      phase: role === 'planner' || role === 'knowledge_graph' ? 'planning' : role === 'web_research' || role === 'academic_research' || role === 'code_analysis' ? 'source_collection' : role === 'source_verification' ? 'verification' : role === 'citation_manager' ? 'citation_assembly' : role === 'report_writer' ? 'report_drafting' : role === 'timeline_builder' ? 'verification' : 'final_synthesis',
      status: 'pending',
      title: roleTitles[role as DeepResearchAgentRole],
      content: '',
    },
  ]);

  return Object.fromEntries(entries) as Record<DeepResearchAgentRole, DeepResearchRoleResult>;
};

const summarizeSources = (sources: DeepResearchSource[]) => {
  if (sources.length === 0) {
    return 'No sources available.';
  }

  return sources
    .map((source) => `- [${source.id}] ${source.title}${source.url ? ` — ${source.url}` : ''}${source.snippet ? `\n  ${source.snippet}` : ''}`)
    .join('\n');
};

const joinParts = (...parts: Array<string | null | undefined>) => parts.filter(Boolean).join('\n\n');

export async function runDeepResearchOrchestration(input: DeepResearchRunInput): Promise<DeepResearchRunResult> {
  const roleResults = createInitialResults();
  const limitations: string[] = [];
  let memory: DeepResearchKnowledgeGraphMemory = createEmptyKnowledgeGraphMemory();
  let allSources: DeepResearchSource[] = [...(input.knowledgeBaseContext?.sources || [])];

  const emit = async (update: DeepResearchStageUpdate) => {
    roleResults[update.role] = {
      ...roleResults[update.role],
      phase: update.phase,
      status: update.status,
      title: update.title,
      content: update.content || roleResults[update.role].content,
      metadata: update.metadata,
      modelId: input.modelsByRole[update.role]?.id,
    };
    await input.onStageUpdate?.(update);
  };

  const executeRole = async (params: {
    role: DeepResearchAgentRole;
    phase: DeepResearchRoleResult['phase'];
    systemPrompt: string;
    userPrompt: string;
    fallbackContent: string;
    metadata?: Record<string, unknown>;
  }) => {
    const { role, phase, systemPrompt, userPrompt, fallbackContent, metadata } = params;

    await emit({
      role,
      phase,
      status: 'running',
      title: roleTitles[role],
      content: fallbackContent,
      metadata,
    });

    try {
      const response = await input.callModel({
        role,
        phase,
        model: input.modelsByRole[role],
        systemPrompt,
        userPrompt,
      });
      const content = response.content.trim() || fallbackContent;
      memory = updateKnowledgeGraphMemory({ memory, content });

      await emit({
        role,
        phase,
        status: 'complete',
        title: roleTitles[role],
        content,
        metadata,
      });

      return content;
    } catch (error: any) {
      const message = error?.message || `${roleTitles[role]} failed.`;
      limitations.push(`${roleTitles[role]} was incomplete: ${message}`);
      await emit({
        role,
        phase,
        status: 'failed',
        title: roleTitles[role],
        content: message,
        metadata,
      });
      return null;
    }
  };

  await emit({
    role: 'orchestrator',
    phase: 'planning',
    status: 'running',
    title: roleTitles.orchestrator,
    content: 'Coordinating the research pipeline, validation steps, and final report handoff.',
    metadata: { overallStatus: 'researching' },
  });

  const kbSummary = input.knowledgeBaseContext?.summary || 'No knowledge base context was attached.';
  const projectContext = input.projectContext || 'No project-specific code context was attached.';

  const plan = await executeRole({
    role: 'planner',
    phase: 'planning',
    fallbackContent: 'Building a research plan and evidence map…',
    systemPrompt:
      'You are the planner agent in a deep research workflow. Produce a compact research plan with these sections: Objective, Research questions, Evidence needs, Search plan, and Risks. Focus on what needs to be proven, compared, or validated.',
    userPrompt: joinParts(
      `User request:\n${input.prompt}`,
      `Knowledge base context:\n${kbSummary}`,
      `Project context:\n${projectContext}`
    ),
  });

  const planText = plan || `Objective: ${input.prompt}`;
  memory = updateKnowledgeGraphMemory({ memory, content: planText, sources: input.knowledgeBaseContext?.sources });

  await executeRole({
    role: 'knowledge_graph',
    phase: 'planning',
    fallbackContent: 'Tracking entities, claims, and open questions for this run…',
    systemPrompt:
      'You maintain run-scoped knowledge graph memory. Extract entities, claims, events, code references, and unresolved questions as concise bullet lists.',
    userPrompt: joinParts(
      `Research prompt:\n${input.prompt}`,
      `Research plan:\n${planText}`,
      `Knowledge base context:\n${kbSummary}`
    ),
    metadata: {
      entityCount: memory.entities.length,
      unresolvedQuestionCount: memory.unresolvedQuestions.length,
    },
  });

  const [webSources, academicSources] = await Promise.all([
    input.webSearch(input.prompt, 'web').catch(() => {
      limitations.push('Web search results were unavailable.');
      return [];
    }),
    input.webSearch(`${input.prompt} research papers`, 'academic').catch(() => {
      limitations.push('Academic search results were unavailable.');
      return [];
    }),
  ]);

  allSources = [...allSources, ...webSources, ...academicSources];
  const { validSources } = validateAndNormalizeSources(allSources);
  allSources = validSources;

  const webResearch = await executeRole({
    role: 'web_research',
    phase: 'source_collection',
    fallbackContent: 'Collecting and distilling broad web sources…',
    systemPrompt:
      'You are the web research agent. Use only the supplied search results and context. Summarize relevant findings, point out recency or consensus, and cite every substantive claim with source ids like [S1].',
    userPrompt: joinParts(
      `User request:\n${input.prompt}`,
      `Research plan:\n${planText}`,
      `Validated web sources:\n${summarizeSources(validSources.filter((source) => source.type === 'web' || source.type === 'knowledge_base'))}`
    ),
    metadata: { sourceCount: validSources.filter((source) => source.type === 'web' || source.type === 'knowledge_base').length },
  });

  const academicResearch = await executeRole({
    role: 'academic_research',
    phase: 'source_collection',
    fallbackContent: 'Reviewing academic and higher-rigor sources…',
    systemPrompt:
      'You are the academic research agent. Prioritize higher-rigor evidence, note confidence caveats, and cite every claim with source ids like [S1].',
    userPrompt: joinParts(
      `User request:\n${input.prompt}`,
      `Research plan:\n${planText}`,
      `Validated academic sources:\n${summarizeSources(validSources.filter((source) => source.type === 'academic'))}`
    ),
    metadata: { sourceCount: validSources.filter((source) => source.type === 'academic').length },
  });

  const codeAnalysis = await executeRole({
    role: 'code_analysis',
    phase: 'source_collection',
    fallbackContent: 'Analyzing project and code context relevant to the request…',
    systemPrompt:
      'You are the code analysis agent. Use the supplied project context and knowledge base context to surface implementation details, code constraints, and relevant references. Clearly separate confirmed code evidence from assumptions.',
    userPrompt: joinParts(
      `User request:\n${input.prompt}`,
      `Research plan:\n${planText}`,
      `Project context:\n${projectContext}`,
      `Knowledge base context:\n${kbSummary}`
    ),
    metadata: {
      sourceCount: input.knowledgeBaseContext?.sources.length || 0,
      verificationStatus: 'pending',
    },
  });

  if (validSources.length === 0) {
    limitations.push('No validated external or knowledge-base sources were retained after normalization.');
  }

  const verification = await executeRole({
    role: 'source_verification',
    phase: 'verification',
    fallbackContent: 'Cross-checking findings against the validated evidence set…',
    systemPrompt:
      'You are the source verification agent. Review the synthesized findings and validated source list. Return Supported claims, Disputed or weakly supported claims, Missing evidence, and Verification notes. Cite every supported or disputed claim with source ids.',
    userPrompt: joinParts(
      `User request:\n${input.prompt}`,
      `Research plan:\n${planText}`,
      `Web research findings:\n${webResearch || 'No web findings available.'}`,
      `Academic research findings:\n${academicResearch || 'No academic findings available.'}`,
      `Code analysis findings:\n${codeAnalysis || 'No code-analysis findings available.'}`,
      `Validated sources:\n${summarizeSources(validSources)}`
    ),
    metadata: {
      sourceCount: validSources.length,
      verificationStatus: 'verified',
    },
  });

  const timeline = await executeRole({
    role: 'timeline_builder',
    phase: 'verification',
    fallbackContent: 'Building a timeline from the strongest dated evidence…',
    systemPrompt:
      'You are the timeline builder. Produce a concise timeline section using dated or ordered events. If the evidence is sparse, say so clearly and only include supported milestones. Cite timeline points with source ids.',
    userPrompt: joinParts(
      `User request:\n${input.prompt}`,
      `Verified findings:\n${verification || 'Verification output unavailable.'}`,
      `Validated sources:\n${summarizeSources(validSources)}`
    ),
  });

  const citationIndex = await executeRole({
    role: 'citation_manager',
    phase: 'citation_assembly',
    fallbackContent: 'Preparing the canonical citation index and unsupported-claim guardrails…',
    systemPrompt:
      'You are the citation manager. Produce a clean citation index, source-group summary, and unsupported-claim warnings using the validated source list. Keep it compact.',
    userPrompt: joinParts(
      `Validated sources:\n${summarizeSources(validSources)}`,
      `Verification notes:\n${verification || 'Verification output unavailable.'}`
    ),
    metadata: {
      citationStatus: validSources.length > 0 ? 'pending' : 'failed',
      sourceCount: validSources.length,
    },
  });

  const reportPrompt = joinParts(
    `User request:\n${input.prompt}`,
    `Research plan:\n${planText}`,
    `Web research:\n${webResearch || 'Unavailable.'}`,
    `Academic research:\n${academicResearch || 'Unavailable.'}`,
    `Code analysis:\n${codeAnalysis || 'Unavailable.'}`,
    `Verification:\n${verification || 'Unavailable.'}`,
    `Timeline:\n${timeline || 'Unavailable.'}`,
    `Citation index:\n${citationIndex || 'Unavailable.'}`,
    `Validated sources:\n${summarizeSources(validSources)}`,
    'Write a polished markdown report with this exact structure: # Title, ## Executive summary, ## Key findings, ## Thematic sections, ## Timeline, ## Confidence summary, ## Sources / citations, ## Methodology / limitations note. Cite every substantive statement with source ids like [S1]. If evidence is weak or missing, explicitly mark the section as limited or unsupported.'
  );

  let reportDraft =
    (await executeRole({
      role: 'report_writer',
      phase: 'report_drafting',
      fallbackContent: 'Drafting the final report with citations…',
      systemPrompt:
        'You are the final report writer in a deep research workflow. Write clearly, stay evidence-bound, cite every major claim, and do not invent unsupported details.',
      userPrompt: reportPrompt,
      metadata: {
        citationStatus: 'pending',
        sourceCount: validSources.length,
      },
    })) || '## Executive summary\nUnable to complete the report draft.';

  let citationCheck = validateReportCitations(reportDraft, validSources);
  if (citationCheck.status === 'failed') {
    limitations.push('The first report draft did not satisfy citation enforcement, so the writer retried once.');
    const retryDraft = await executeRole({
      role: 'report_writer',
      phase: 'report_drafting',
      fallbackContent: 'Retrying report drafting to repair missing or invalid citations…',
      systemPrompt:
        'Repair the report so every major section contains valid citations that reference only the supplied source ids. Do not introduce new source ids.',
      userPrompt: joinParts(
        reportPrompt,
        `Citation check issues:\n- Missing sections: ${citationCheck.missingSections.join(', ') || 'none'}\n- Unknown citations: ${citationCheck.unknownCitations.join(', ') || 'none'}`
      ),
      metadata: {
        citationStatus: 'partial',
        sourceCount: validSources.length,
      },
    });

    if (retryDraft) {
      reportDraft = retryDraft;
      citationCheck = validateReportCitations(reportDraft, validSources);
    }
  }

  if (citationCheck.status !== 'passed') {
    limitations.push('Some report sections remain partially supported or under-cited after validation.');
  }

  const confidence = computeDeepResearchConfidence({
    sources: validSources,
    citationCheck,
    roleResults: Object.values(roleResults),
    limitations,
  });

  const finalReport = assembleFinalResearchReport({
    reportDraft,
    confidence,
    sources: validSources,
    limitations,
  });

  memory = updateKnowledgeGraphMemory({ memory, content: finalReport, sources: validSources });

  const finalSummary = [
    `Research workflow complete with ${validSources.length} validated source${validSources.length === 1 ? '' : 's'}.`,
    `Confidence: ${confidence.label.toUpperCase()} (${confidence.score}/100).`,
    citationCheck.status === 'passed'
      ? 'Citation enforcement passed for the final report.'
      : `Citation enforcement returned ${citationCheck.status}; see limitations in the report.`,
  ].join(' ');

  await emit({
    role: 'orchestrator',
    phase: 'final_synthesis',
    status: citationCheck.status === 'failed' ? 'failed' : 'complete',
    title: roleTitles.orchestrator,
    content: finalSummary,
    metadata: {
      overallStatus: citationCheck.status === 'failed' ? 'failed' : 'complete',
      confidenceScore: confidence.score,
      citationStatus: citationCheck.status,
      verificationStatus: verification ? 'verified' : 'issues',
      sourceCount: validSources.length,
      limitations,
    },
  });

  await emit({
    role: 'report_writer',
    phase: 'report_drafting',
    status: citationCheck.status === 'failed' ? 'failed' : 'complete',
    title: roleTitles.report_writer,
    content: finalReport,
    metadata: {
      overallStatus: citationCheck.status === 'failed' ? 'failed' : 'complete',
      confidenceScore: confidence.score,
      citationStatus: citationCheck.status,
      verificationStatus: verification ? 'verified' : 'issues',
      sourceCount: validSources.length,
      isFinalReport: true,
      limitations,
      sources: validSources,
      confidence,
      citationCheck,
    },
  });

  return {
    status: citationCheck.status === 'failed' ? 'failed' : 'complete',
    roleResults,
    sources: validSources,
    citationCheck,
    confidence,
    memory,
    finalReport,
    limitations,
  };
}
